import React from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import DetailField from './DetailField';
import TicketCommentsPanel from './TicketCommentsPanel';
import TicketDetailSection from './TicketDetailSection';
import TicketScoreHistory from './TicketScoreHistory';
import TicketWatchPanel from './TicketWatchPanel';
import {
  type CxTicketRow,
  RISK_TIER_COLORS,
  describeTierChange,
  formatAiSignalLabel,
  formatAud,
  formatBreakdownLabel,
  formatConfidence,
  formatCount,
  formatDateTime,
  formatEmptyValue,
  formatHours,
  formatKeywordList,
  formatMinutesAsDuration,
  formatPercentile,
  formatRiskFlagLabel,
  getAiSignalTokens,
  humanize,
  isEmergingSignal,
  joinDetails,
  toDisplaySubject,
  toJourneyPhaseLabel,
  zendeskTicketUrl,
} from './ticketRiskRows';

const TWO_COLUMN_GRID = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 } as const;
const THREE_COLUMN_GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
  gap: 1.5,
} as const;

/** Wide enough for "Keyword signal", so the factor labels line up in one column. */
const FACTOR_TYPE_CHIP_WIDTH = 116;

const toCheckInDetail = (checkInDays: number | null) => {
  if (checkInDays == null) {
    return null;
  }

  return checkInDays <= 0 ? 'checks in today' : `checks in ${formatCount(checkInDays, 'day')}`;
};

const toLastStayDetail = (daysSinceLastCheckOut: number | null) => {
  if (daysSinceLastCheckOut == null) {
    return null;
  }

  return daysSinceLastCheckOut <= 0
    ? 'checked out today'
    : `last stay ${formatCount(daysSinceLastCheckOut, 'day')} ago`;
};

/**
 * The same label as the grid chip, so the summary and the column never disagree. An Unknown
 * phase contributes only its uncapped check-in or last-stay context, if any.
 */
const toJourneyDetail = (ticket: CxTicketRow): string | null => {
  if (ticket.journeyPhase === 'Unknown') {
    return toCheckInDetail(ticket.checkInDays) ?? toLastStayDetail(ticket.daysSinceLastCheckOut);
  }

  return toJourneyPhaseLabel(ticket);
};

/**
 * One-line, fact-based digest so a lead can triage without reading the grids below. Facts
 * only — the AI's read of the conversation renders alongside it, clearly labelled.
 */
const toTicketSummary = (ticket: CxTicketRow) =>
  joinDetails([
    ticket.ticketReason ?? 'Uncategorised',
    ticket.subcategory,
    ticket.ticketStatus,
    ticket.hoursSinceOpen > 0 ? `open ${formatHours(ticket.hoursSinceOpen)}` : null,
    ticket.requesterWaitMinutes > 0 ? `customer waiting ${formatMinutesAsDuration(ticket.requesterWaitMinutes)}` : null,
    toJourneyDetail(ticket),
    ticket.societeTier ? `${ticket.societeTier} member` : null,
    ticket.customerLifetimeValue == null ? null : `${formatAud(ticket.customerLifetimeValue)} LTV`,
  ]);

type RiskFactorType = 'Classification' | 'Ticket flag' | 'Keyword signal' | 'AI signal' | 'Operational' | 'Customer';

/** One colour per factor type, so the source of each factor reads at a glance. */
const RISK_FACTOR_TYPE_COLORS: Record<
  RiskFactorType,
  'primary' | 'warning' | 'error' | 'secondary' | 'info' | 'success'
> = {
  Classification: 'primary',
  'Ticket flag': 'warning',
  'Keyword signal': 'error',
  'AI signal': 'secondary',
  Operational: 'info',
  Customer: 'success',
};

interface RiskFactorRow {
  type: RiskFactorType;
  label: string;
}

/** One row per factor type, in first-appearance order, so ten factors read as five lines. */
const groupRiskFactors = (rows: Array<RiskFactorRow>): Array<{ type: RiskFactorType; labels: Array<string> }> => {
  const groups: Array<{ type: RiskFactorType; labels: Array<string> }> = [];

  for (const row of rows) {
    const group = groups.find((candidate) => candidate.type === row.type);
    if (group) {
      group.labels.push(row.label);
    } else {
      groups.push({ type: row.type, labels: [row.label] });
    }
  }

  return groups;
};

/**
 * Everything that drove the blended score, flattened into one list: the hard score's winning
 * classification, matched Zendesk flags and keyword signals, and the operational and customer
 * inputs that scored points, plus the LLM's own named signals — each labelled with the
 * underlying fact rather than its point value.
 */
const toRiskFactorRows = (ticket: CxTicketRow): Array<RiskFactorRow> => {
  const rows: Array<RiskFactorRow> = [];

  if (ticket.classificationSource) {
    rows.push({
      type: 'Classification',
      label: `${formatBreakdownLabel(ticket.classificationSource)}${
        ticket.classificationLift != null ? ` · ${ticket.classificationLift}x low-CSAT lift` : ''
      }`,
    });
  }
  ticket.matchedRiskFlags.forEach((flag) => rows.push({ type: 'Ticket flag', label: formatRiskFlagLabel(flag) }));
  ticket.matchedKeywordSignals.forEach((signal) =>
    rows.push({ type: 'Keyword signal', label: formatBreakdownLabel(signal) }),
  );
  // Named from a fixed vocabulary, so an "other:" one is a pattern the scorer had no name for
  // — worth marking rather than hiding, since it is the earliest warning the model gives.
  getAiSignalTokens(ticket).forEach((token) =>
    rows.push({
      type: 'AI signal',
      label: `${formatAiSignalLabel(token)}${isEmergingSignal(token) ? ' (emerging)' : ''}`,
    }),
  );

  if (ticket.reassignPoints > 0) {
    rows.push({ type: 'Operational', label: `Reassigned ${formatCount(ticket.reassignCount, 'time')}` });
  }
  if (ticket.agingPoints > 0) {
    rows.push({ type: 'Operational', label: `Open ${formatHours(ticket.hoursSinceOpen)}` });
  }
  if (ticket.awaitingReviewPoints > 0) {
    rows.push({ type: 'Operational', label: `Awaiting review ${formatHours(ticket.hoursAwaitingReview)}` });
  }
  if (ticket.requesterWaitPoints > 0) {
    rows.push({
      type: 'Operational',
      label: `Customer waiting ${formatMinutesAsDuration(ticket.requesterWaitMinutes)}`,
    });
  }
  if (ticket.staleContactPoints > 0) {
    rows.push({ type: 'Operational', label: `No contact for ${formatHours(ticket.hoursSinceLastContact)}` });
  }
  if (ticket.internalCommentPoints > 0) {
    rows.push({ type: 'Operational', label: formatCount(ticket.internalComments, 'internal comment') });
  }
  if (ticket.reopenPoints > 0) {
    rows.push({ type: 'Operational', label: `Reopened ${formatCount(ticket.reopenCount, 'time')}` });
  }

  if (ticket.inStayPoints > 0) {
    rows.push({ type: 'Customer', label: 'In-stay now' });
  }
  if (ticket.upcomingCheckInPoints > 0) {
    rows.push({
      type: 'Customer',
      label: `Upcoming check-in${ticket.checkInDays != null ? ` (${formatCount(ticket.checkInDays, 'day')})` : ''}`,
    });
  }
  if (ticket.recentStayPoints > 0) {
    rows.push({ type: 'Customer', label: 'Recently checked out' });
  }
  if (ticket.ltvPoints > 0) {
    rows.push({
      type: 'Customer',
      label: `${formatAud(ticket.customerLifetimeValue)} LTV${ticket.ltvBand ? ` (${ticket.ltvBand})` : ''}`,
    });
  }
  if (ticket.societePoints > 0 && ticket.societeTier) {
    rows.push({ type: 'Customer', label: `${ticket.societeTier} member` });
  }
  if (ticket.luxPlusPoints > 0) {
    rows.push({ type: 'Customer', label: 'Lux+ member' });
  }

  return rows;
};

interface Props {
  /** The row to describe; the dialog is closed while this is null. */
  ticket: CxTicketRow | null;
  onClose: () => void;
  /** The signed-in leader's `id_member`, so their own comments can be deleted. */
  currentUserId?: string | null;
}

export default function TicketDetailDialog({ ticket, onClose, currentUserId = null }: Props) {
  const riskFactors = ticket ? toRiskFactorRows(ticket) : [];

  return (
    <Dialog open={ticket !== null} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
      <DialogTitle component="div">
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="flex-start"
          useFlexGap
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="h6">{ticket ? `Ticket ${ticket.id}` : 'Ticket details'}</Typography>
            {ticket && toDisplaySubject(ticket.subject, ticket.id) && (
              <Typography variant="body2" color="text.secondary">
                {toDisplaySubject(ticket.subject, ticket.id)}
              </Typography>
            )}
          </Box>
          {ticket && (
            <Stack alignItems={{ xs: 'flex-start', sm: 'flex-end' }} spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h3" fontWeight={700} color={`${RISK_TIER_COLORS[ticket.riskTier]}.main`}>
                  {ticket.riskScore}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  / 100
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {joinDetails([
                  `${ticket.riskTier} risk`,
                  ticket.percentile == null ? null : formatPercentile(ticket.percentile),
                  ticket.criticalRank === null ? null : `critical #${ticket.criticalRank}`,
                ])}
              </Typography>
              {(ticket.isNew || ticket.legalHold || ticket.divergent || ticket.resolutionAccepted) && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {ticket.isNew && (
                    <Tooltip title={describeTierChange(ticket) ?? ''}>
                      <Chip size="small" color="secondary" variant="outlined" label="New" />
                    </Tooltip>
                  )}
                  {ticket.legalHold && <Chip size="small" color="error" label="Legal hold" />}
                  {ticket.divergent && <Chip size="small" color="warning" label="Divergent" />}
                  {ticket.resolutionAccepted && <Chip size="small" color="success" label="Resolution accepted" />}
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {ticket && (
          <Stack spacing={3}>
            <TicketDetailSection title="Summary">
              <Stack spacing={1.5}>
                <Typography>{toTicketSummary(ticket)}</Typography>
                {!ticket.hasLlmAssessment ? (
                  <Alert severity="warning">
                    The LLM assessment failed for this ticket — the hard score carries the blended score on its own.
                  </Alert>
                ) : (
                  ticket.llmReasoning && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {joinDetails([
                          'AI read of the conversation',
                          ticket.llmTrajectory,
                          ticket.llmConfidence == null ? null : `${formatConfidence(ticket.llmConfidence)} confidence`,
                        ])}
                      </Typography>
                      <Typography variant="body2">{ticket.llmReasoning}</Typography>
                    </Box>
                  )
                )}
              </Stack>
            </TicketDetailSection>

            <TicketDetailSection
              title="Watching"
              caption="One leader owns a ticket at a time — take it over if it's yours now."
            >
              <TicketWatchPanel ticket={ticket} />
            </TicketDetailSection>

            <TicketDetailSection
              title="Comments"
              caption="Anything the other leaders should know. You can delete your own comments."
            >
              <TicketCommentsPanel ticketId={ticket.id} currentUserId={currentUserId} />
            </TicketDetailSection>

            <TicketDetailSection title="Score history">
              <TicketScoreHistory ticketId={ticket.id} />
            </TicketDetailSection>

            <TicketDetailSection
              title="Risk factors"
              caption={`Hard ${ticket.hardScore} / 100 · LLM ${formatEmptyValue(ticket.llmScore)} · blended ${
                ticket.riskScore
              }`}
            >
              {riskFactors.length === 0 ? (
                <Typography color="text.disabled">None matched</Typography>
              ) : (
                <Stack spacing={1}>
                  {groupRiskFactors(riskFactors).map((group) => (
                    <Stack key={group.type} direction="row" spacing={1.5} alignItems="flex-start">
                      <Chip
                        size="small"
                        variant="outlined"
                        color={RISK_FACTOR_TYPE_COLORS[group.type]}
                        label={group.type}
                        sx={{ minWidth: FACTOR_TYPE_CHIP_WIDTH, flexShrink: 0 }}
                      />
                      <Typography variant="body2" sx={{ pt: 0.25 }}>
                        {group.labels.join(' · ')}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </TicketDetailSection>

            <TicketDetailSection title="Ticket and customer">
              <Box sx={TWO_COLUMN_GRID}>
                <DetailField label="Status">{ticket.ticketStatus}</DetailField>
                <DetailField label="Ownership">{joinDetails([ticket.team, ticket.assignee])}</DetailField>
                <DetailField label="Team Leader">{formatEmptyValue(ticket.teamLeader)}</DetailField>
                <DetailField label="Reason">{joinDetails([ticket.ticketReason, ticket.subcategory])}</DetailField>
                <DetailField label="Priority / Triage">
                  {joinDetails([humanize(ticket.priority), humanize(ticket.triageCategory)])}
                </DetailField>
                <DetailField label="Sentiment">{formatEmptyValue(ticket.sentiment)}</DetailField>
                <DetailField label="Authority Mentions">
                  {formatKeywordList(ticket.authorityKeywords)}
                  {ticket.authorityConfirmed ? ' (LLM confirmed)' : ''}
                </DetailField>
                <DetailField label="Journey">{toJourneyDetail(ticket)}</DetailField>
                <DetailField label="Société Tier / LTV">
                  {joinDetails([
                    ticket.societeTier,
                    ticket.customerLifetimeValue == null
                      ? null
                      : `${formatAud(ticket.customerLifetimeValue)}${ticket.ltvBand ? ` (${ticket.ltvBand})` : ''}`,
                  ])}
                </DetailField>
              </Box>
            </TicketDetailSection>

            <TicketDetailSection title="Activity">
              <Box sx={THREE_COLUMN_GRID}>
                <DetailField label="Created">{formatDateTime(ticket.created)}</DetailField>
                <DetailField label="Last Updated">{formatDateTime(ticket.lastUpdated)}</DetailField>
                <DetailField label="Open For">{formatHours(ticket.hoursSinceOpen)}</DetailField>
                <DetailField label="Awaiting Review">{formatHours(ticket.hoursAwaitingReview)}</DetailField>
                <DetailField label="Customer Waiting">
                  {formatMinutesAsDuration(ticket.requesterWaitMinutes)}
                </DetailField>
                <DetailField label="Last Agent Reply">{formatDateTime(ticket.lastAgentReplyAt)}</DetailField>
                <DetailField label="Replies / Internal">
                  {ticket.replies} / {ticket.internalComments}
                </DetailField>
                <DetailField label="Agents Touched">{ticket.agentsTouched}</DetailField>
                <DetailField label="Reassigns">{ticket.reassignCount}</DetailField>
                <DetailField label="Reopens">{formatCount(ticket.reopenCount, 'time')}</DetailField>
              </Box>
            </TicketDetailSection>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Close
        </Button>
        <Button
          component="a"
          href={ticket ? zendeskTicketUrl(ticket.id) : undefined}
          target="_blank"
          rel="noreferrer"
          variant="contained"
          color="primary"
        >
          Open in Zendesk
        </Button>
      </DialogActions>
    </Dialog>
  );
}
